import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrandProvider, useBrand } from '@/contexts/BrandContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PortalLayout } from '@/components';
import {
  HomePage,
  CategoryPage,
  SectionPage,
  ArticlePage,
  SearchPage,
  RequestPage,
  MyRequestsPage,
  RequestDetailPage,
  LoginPage,
  VerifyPage,
  NotFoundPage,
} from '@/pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Inner component that has access to brand context
function AppContent() {
  const { brand } = useBrand();
  const themeMode = brand?.theme_mode ?? 'light';

  return (
    <ThemeProvider themeMode={themeMode}>
      <BrowserRouter>
        <Routes>
          <Route element={<PortalLayout />}>
            <Route index element={<HomePage />} />
            {/* Clean URL format: /:categorySlug/:sectionSlug/:articleSlug */}
            <Route path=":categorySlug/:sectionSlug/:articleSlug" element={<ArticlePage />} />
            <Route path=":categorySlug/:sectionSlug" element={<SectionPage />} />
            <Route path=":categorySlug" element={<CategoryPage />} />
            {/* Reserved routes */}
            <Route path="search" element={<SearchPage />} />
            <Route path="requests" element={<MyRequestsPage />} />
            <Route path="requests/new" element={<RequestPage />} />
            <Route path="requests/:ticketId" element={<RequestDetailPage />} />
            <Route path="auth/login" element={<LoginPage />} />
            <Route path="auth/verify" element={<VerifyPage />} />
            {/* Legacy routes for backwards compatibility */}
            <Route path="categories/:slug" element={<CategoryPage />} />
            <Route path="categories/:categorySlug/sections/:slug" element={<SectionPage />} />
            <Route path="categories/:categorySlug/sections/:sectionSlug/articles/:slug" element={<ArticlePage />} />
            <Route path="sections/:slug" element={<SectionPage />} />
            <Route path="articles/:slug" element={<ArticlePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandProvider>
        <AppContent />
      </BrandProvider>
    </QueryClientProvider>
  );
}

export default App;
